/**
 * Task Assignment Utilities
 * Shared helpers for displaying assignment direction, initials, duration,
 * overdue age, and category chip styles across sidebar and calendar.
 */

import type { Task, TaskListItemDto } from '@signalvote/shared';
import { differenceInDays, differenceInWeeks, differenceInMonths, parseISO } from 'date-fns';

export type AssignmentDirection = 'inbound' | 'outbound' | null;

/**
 * Determine whether a task was delegated to someone else (outbound),
 * assigned to the current user by someone else (inbound), or is the user's own task.
 */
export function getAssignmentDirection(task: Task, currentUserId?: string): AssignmentDirection {
  if (!currentUserId) return null;

  // Outbound: I created it and assigned it to someone else
  if (task.createdById === currentUserId && task.assignedToId && task.assignedToId !== currentUserId) {
    return 'outbound';
  }
  // Inbound: someone else created it and assigned it to me
  if (task.assignedToId === currentUserId && task.createdById && task.createdById !== currentUserId) {
    return 'inbound';
  }
  return null;
}

/**
 * Extract initials from a full name.
 * "Sarah Miller" → "SM", "John" → "J"
 */
export function getInitials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return parts[0][0].toUpperCase();
}

/**
 * Format duration in minutes to a short label.
 * 15 → "15m", 30 → "30m", 60 → "1h", 90 → "1.5h", 120 → "2h"
 */
export function formatDurationShort(minutes?: number): string | null {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  if (hours % 1 === 0) return `${hours}h`;
  return `${hours.toFixed(1)}h`;
}

/**
 * Format how long ago a due date was, for display in the overdue section.
 * Returns null if the date is today or in the future.
 * "1 day ago", "5 days ago", "2 weeks ago", "3 months ago"
 */
export function formatOverdueAge(dueDate?: string): string | null {
  if (!dueDate) return null;

  const due = parseISO(dueDate);
  const now = new Date();
  // Strip time for clean day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  if (dueDay >= today) return null;

  const days = differenceInDays(today, dueDay);
  if (days === 1) return '1 day ago';
  if (days < 14) return `${days} days ago`;

  const weeks = differenceInWeeks(today, dueDay);
  if (weeks < 8) return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;

  const months = differenceInMonths(today, dueDay);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

const CUSTOM_PALETTE = [
  'bg-lime-100 text-lime-700',
  'bg-orange-100 text-orange-700',
  'bg-cyan-100 text-cyan-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-yellow-100 text-yellow-700',
  'bg-indigo-100 text-indigo-700',
  'bg-pink-100 text-pink-700',
  'bg-red-100 text-red-700',
  'bg-blue-100 text-blue-700',
  'bg-stone-100 text-stone-700',
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * Get color-only Tailwind classes for a category.
 * Returns bg + text color classes without sizing or spacing.
 * Useful for contexts where the consumer controls its own sizing (e.g., FilterChip).
 */
export function getCategoryColorClasses(categoryName?: string): string {
  if (!categoryName) return '';

  const normalized = categoryName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  switch (normalized) {
    case 'fieldops':
      return 'bg-emerald-100 text-emerald-700';
    case 'fundraising':
      return 'bg-amber-100 text-amber-700';
    case 'communications':
      return 'bg-sky-100 text-sky-700';
    case 'data':
      return 'bg-purple-100 text-purple-700';
    case 'events':
      return 'bg-rose-100 text-rose-700';
    case 'admin':
      return 'bg-slate-100 text-slate-700';
    case 'outreach':
      return 'bg-teal-100 text-teal-700';
    default: {
      const colorIdx = hashString(normalized) % CUSTOM_PALETTE.length;
      return CUSTOM_PALETTE[colorIdx];
    }
  }
}

/**
 * Get Tailwind classes for a category-colored chip.
 * Includes sizing, spacing, and color. For compact inline chips (TaskCard, EventBlock).
 */
export function getCategoryChipClasses(categoryName?: string): string {
  if (!categoryName) return '';
  const base = 'text-[0.6rem] px-1.5 py-0.5 rounded font-medium';
  return `${base} ${getCategoryColorClasses(categoryName)}`;
}

/**
 * Abbreviate a category name for compact display.
 * Defaults use known abbreviations. Custom categories show full name (truncated).
 */
export function getCategoryAbbreviation(categoryName?: string): string | null {
  if (!categoryName) return null;

  const normalized = categoryName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  switch (normalized) {
    case 'fieldops':
      return 'Ops';
    case 'fundraising':
      return 'Fund';
    case 'communications':
      return 'Comms';
    case 'data':
      return 'Data';
    case 'events':
      return 'Events';
    case 'admin':
      return 'Admin';
    case 'outreach':
      return 'Reach';
    default:
      return categoryName.length > 8 ? categoryName.slice(0, 8) + '...' : categoryName;
  }
}

/**
 * Direction-aware color palette for task blocks.
 * Each direction (inbound/outbound/default) has its own color scheme,
 * with a separate completed state for finished tasks.
 */
export interface TaskDirectionPalette {
  block: string;
  title: string;
  secondary: string;
  checkbox: string;
  ring: string;
  handle: string;
  handleActive: string;
}

const DIRECTION_PALETTES: Record<'default' | 'inbound' | 'outbound' | 'completed', TaskDirectionPalette> = {
  default: {
    block: 'bg-amber-100 border-l-4 border-l-amber-500',
    title: 'text-amber-800',
    secondary: 'text-amber-700',
    checkbox: 'accent-amber-600',
    ring: 'ring-2 ring-amber-400 shadow-lg',
    handle: 'bg-amber-400',
    handleActive: 'bg-amber-500',
  },
  inbound: {
    block: 'bg-blue-50 border-l-4 border-l-blue-500',
    title: 'text-blue-800',
    secondary: 'text-blue-600',
    checkbox: 'accent-blue-600',
    ring: 'ring-2 ring-blue-400 shadow-lg',
    handle: 'bg-blue-400',
    handleActive: 'bg-blue-500',
  },
  outbound: {
    block: 'bg-violet-50 border-l-4 border-l-violet-500',
    title: 'text-violet-800',
    secondary: 'text-violet-600',
    checkbox: 'accent-violet-600',
    ring: 'ring-2 ring-violet-400 shadow-lg',
    handle: 'bg-violet-400',
    handleActive: 'bg-violet-500',
  },
  completed: {
    block: 'bg-gray-200 border-l-4 border-l-gray-400 opacity-80',
    title: 'text-gray-600 line-through',
    secondary: 'text-gray-500',
    checkbox: 'accent-gray-600',
    ring: 'ring-2 ring-gray-400 shadow-lg',
    handle: 'bg-gray-400',
    handleActive: 'bg-gray-500',
  },
};

/**
 * Resolve a direction + completed state to a full color palette.
 * Used by TaskBlock, DragOverlay, and anywhere task direction affects styling.
 */
export function getTaskPalette(isCompleted: boolean, direction: AssignmentDirection): TaskDirectionPalette {
  if (isCompleted) return DIRECTION_PALETTES.completed;
  return DIRECTION_PALETTES[direction ?? 'default'];
}

/**
 * Get initials of the "other person" in a task assignment.
 * For outbound tasks, shows the assignee. For inbound, shows the creator.
 */
export function getAssigneeInitials(task: Task, direction: AssignmentDirection): string | null {
  if (direction === 'outbound') return getInitials(task.assignedToName);
  if (direction === 'inbound') return getInitials(task.createdByName);
  return null;
}

/**
 * Format a volunteer's name for compact display.
 * "Sarah" + "Miller" -> "Sarah M.", "John" + undefined -> "John"
 */
export function formatVolunteerDisplayName(firstName: string, lastName?: string | null): string {
  if (lastName) return `${firstName} ${lastName[0]}.`;
  return firstName;
}

/**
 * Check whether a URL is external (http/https) vs an internal app route.
 */
export function isExternalUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://');
}

/**
 * Map a TaskListItemDto from the API to the shared Task type.
 * Used by both TaskSidebar and ShiftsPage to avoid duplicating the mapping.
 */
export function mapTaskListItemToTask(item: TaskListItemDto): Task {
  return {
    id: item.id,
    title: item.title,
    description: '',
    source: item.source,
    status: item.status,
    dueDate: item.dueDate ?? undefined,
    dueTime: item.dueTime ?? undefined,
    durationMinutes: item.durationMinutes,
    assignedToId: item.assignedToId ?? undefined,
    assignedToName: item.assignedToName ?? undefined,
    categoryId: item.categoryId ?? undefined,
    categoryName: item.categoryName ?? undefined,
    actionUrl: item.actionUrl ?? undefined,
    isOpen: item.isOpen,
    createdById: item.createdById ?? undefined,
    createdByName: item.createdByName ?? undefined,
    acknowledgedAt: item.acknowledgedAt ?? undefined,
  };
}
